import "./qa-premium.css";
import { useLocation } from "wouter";
import { useTranslation } from "react-i18next";
import { trpc } from "@/utils/trpc";
import { useQaPerms, formatDate, BAND_META } from "./qa-shared";
import { Calendar, Send, Eye, ClipboardList, CheckCircle2, ShieldCheck, Kanban, CalendarPlus } from "lucide-react";
import { FioriPageHeader } from "@/components/fiori";
import { Button } from "@/components/ui/button";

const PILL: Record<string, string> = {
  scheduled: "pill-scheduled", questionnaire_submitted: "pill-submitted", under_review: "pill-review",
  assessment_submitted: "pill-assessed", report_approved: "pill-approved", cancelled: "pill-cancelled",
};

export default function QaDashboard() {
  const { t } = useTranslation();
  const [, setLocation] = useLocation();
  const perms = useQaPerms();
  const { data: visits = [], isLoading } = trpc.qa.visits.list.useQuery({ limit: 50 });
  const { data: basket = [] } = trpc.qa.qag.basket.useQuery(undefined, { enabled: perms.reviewView });

  const counts = visits.reduce((a: Record<string, number>, v: any) => { a[v.status] = (a[v.status] ?? 0) + 1; return a; }, {});

  const completedCount = counts["report_approved"] ?? 0;
  const totalCount = visits.length;
  const inProgressCount = totalCount - completedCount;
  const completionRate = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

  const LABEL: Record<string, string> = {
    scheduled: t("qa.status.scheduled"),
    questionnaire_submitted: t("qa.status.submitted"),
    under_review: t("qa.status.underReview"),
    assessment_submitted: t("qa.status.assessment"),
    report_approved: t("qa.status.approved"),
    cancelled: t("qa.status.cancelled"),
  };

  const stats = [
    { key: "scheduled", label: t("qa.stats.scheduled.label"), sub: t("qa.stats.scheduled.sub"), icon: Calendar, bg: "#E8DCF5", fg: "#5B2C93" },
    { key: "questionnaire_submitted", label: t("qa.stats.submitted.label"), sub: t("qa.stats.submitted.sub"), icon: Send, bg: "#DBEAFE", fg: "#2563EB" },
    { key: "under_review", label: t("qa.stats.underReview.label"), sub: t("qa.stats.underReview.sub"), icon: Eye, bg: "#FEF3C7", fg: "#D97706" },
    { key: "assessment_submitted", label: t("qa.stats.assessment.label"), sub: t("qa.stats.assessment.sub"), icon: ClipboardList, bg: "#E8DCF5", fg: "#5B2C93" },
    { key: "report_approved", label: t("qa.stats.approved.label"), sub: t("qa.stats.approved.sub"), icon: CheckCircle2, bg: "#D1FAE5", fg: "#059669" },
  ];

  const visitCard = (v: any) => {
    const band = v.interpretationBand ? BAND_META[v.interpretationBand] : null;
    const showRing = v.status === "report_approved" && v.overallScore != null;
    return (
      <button className="tcard" key={v.id} onClick={() => setLocation(`/qa/visits/${v.id}`)}>
        <div className="main">
          <div className="num">{v.visitNumber}</div>
          <div className="site">{v.siteName ?? `Site #${v.siteId}`}</div>
          <div className="meta">{LABEL[v.status] ?? "Visit"} · {formatDate(v.visitDate)}</div>
        </div>
        {showRing && (
          <div className="ring" style={{ background: (band?.color ?? "var(--green)") + "1a", color: band?.color ?? "var(--green)" }}>
            {Math.round(Number(v.overallScore))}%
          </div>
        )}
        <span className={`pill ${PILL[v.status]}`}>{LABEL[v.status]}</span>
      </button>
    );
  };

  return (
    <div className="-mx-4 md:-mx-6 -mb-4 md:-mb-6 min-h-[calc(100vh-4rem)] bg-white px-4 md:px-6">
      <FioriPageHeader
        title={t("qa.dashboard.title")}
        subtitle={t("qa.dashboard.subtitle")}
        icon={<ShieldCheck className="h-5 w-5" />}
        actions={
          <>
            <Button variant="outline" size="sm" onClick={() => setLocation("/qa/visits")} className="gap-1.5 text-[#6B6B6B] border-[#E0E0E0]">
              <Kanban className="h-3.5 w-3.5" /> {t("qa.dashboard.visits")}
            </Button>
            {perms.visitManage && (
              <Button size="sm" onClick={() => setLocation("/qa/visits/new")} className="gap-1.5 bg-[#5B2C93] hover:bg-[#3D1C5E]">
                <CalendarPlus className="h-3.5 w-3.5" /> {t("qa.dashboard.scheduleVisit")}
              </Button>
            )}
          </>
        }
      />

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 px-0 pb-4">
        {stats.map((s) => (
          <div
            key={s.key}
            className="bg-white border border-[#E0E0E0] rounded-lg px-4 py-3 transition-all hover:border-[#5B2C93]/30"
          >
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-lg flex items-center justify-center" style={{ background: s.bg }}>
                <s.icon className="h-4 w-4" style={{ color: s.fg }} />
              </div>
              <div>
                <p className="text-xs text-[#6B6B6B] font-medium">{s.label}</p>
                <p className="text-xl font-semibold text-[#2C2C2C]">{counts[s.key] ?? 0}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="qa-premium full">
        <div className="wrap wrap-grid">
        <div className="wrap-left">
          {perms.reviewView && (
            <>
              <div className="sec"><i className="ti ti-inbox" /> {t("qa.dashboard.qagBasket")} {basket.length > 0 && <span className="c">{basket.length} {t("qa.dashboard.awaiting")}</span>}</div>
              {basket.length === 0
                ? <div className="empty"><i className="ti ti-inbox" /> {t("qa.dashboard.noQuestionnaires")}</div>
                : basket.map(visitCard)}
            </>
          )}

          <div className="sec">
            <i className="ti ti-clipboard-check" /> {t("qa.dashboard.recentVisits")}
            <span className="viewall" onClick={() => setLocation("/qa/visits")}>{t("qa.dashboard.viewAll")} <i className="ti ti-arrow-right" /></span>
          </div>
          {isLoading
            ? <div className="loading"><span className="spin" /></div>
            : visits.length === 0
              ? <div className="empty"><i className="ti ti-clipboard-check" />{t("qa.dashboard.noVisits")}</div>
              : visits.slice(0, 8).map(visitCard)}
        </div>

        <div className="wrap-right">
          <div className="qstat-box">
            <div className="qstat-header">
              {t("qa.dashboard.quickStats")}
            </div>
            <div className="qstat-grid">
              <div className="qstat-item">
                <div className="qstat-header-row">
                  <div className="qstat-label">{t("qa.dashboard.totalVisits")}</div>
                  <div className="qstat-value">{totalCount}</div>
                </div>
                <div className="qstat-bar">
                  <div className="qstat-fill" style={{ width: '100%' }} />
                </div>
              </div>
              <div className="qstat-item">
                <div className="qstat-header-row">
                  <div className="qstat-label">{t("qa.dashboard.inProgress")}</div>
                  <div className="qstat-value">{inProgressCount}</div>
                </div>
                <div className="qstat-bar">
                  <div className="qstat-fill" style={{ width: totalCount > 0 ? `${(inProgressCount / totalCount) * 100}%` : '0%' }} />
                </div>
              </div>
              <div className="qstat-item">
                <div className="qstat-header-row">
                  <div className="qstat-label">{t("qa.dashboard.completed")}</div>
                  <div className="qstat-value">{completedCount}</div>
                </div>
                <div className="qstat-bar">
                  <div className="qstat-fill" style={{ width: totalCount > 0 ? `${(completedCount / totalCount) * 100}%` : '0%' }} />
                </div>
              </div>
              <div className="qstat-item">
                <div className="qstat-header-row">
                  <div className="qstat-label">{t("qa.dashboard.completionRate")}</div>
                  <div className="qstat-value">{completionRate}%</div>
                </div>
                <div className="qstat-bar">
                  <div className="qstat-fill" style={{ width: `${completionRate}%` }} />
                </div>
              </div>
            </div>
          </div>
        </div>
        </div>
      </div>
    </div>
  );
}
